import { Snackbar, Alert, Button } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { useRegisterSW } from 'virtual:pwa-register/react'

// Re-check for a freshly-deployed version while the app stays open. An
// installed PWA can run for days without a full reload, so without this the
// new service worker would only be discovered on the next cold start.
const UPDATE_CHECK_INTERVAL_MS = 60 * 60 * 1000 // hourly

/**
 * Surfaces an in-app "Update available" prompt when a new app version has been
 * deployed, so users get new features without uninstalling/reinstalling the
 * PWA. Backed by vite-plugin-pwa's `registerType: 'prompt'`: the new service
 * worker installs and waits; tapping "Update" calls `updateServiceWorker(true)`
 * which skips waiting, activates it, and reloads the page.
 */
export function UpdatePrompt() {
  const { t } = useTranslation()
  const {
    needRefresh: [needRefresh, setNeedRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    onRegisteredSW(_swUrl, registration) {
      if (!registration) return
      window.setInterval(() => {
        registration.update().catch(() => {})
      }, UPDATE_CHECK_INTERVAL_MS)
    },
  })

  if (!needRefresh) return null

  return (
    <Snackbar
      open
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      // Clear the mobile bottom navigation (+ safe-area) so the prompt isn't
      // hidden behind it; sit at the default offset on larger screens.
      sx={{
        bottom: {
          xs: 'calc(56px + env(safe-area-inset-bottom, 0px) + 16px)',
          sm: 24,
        },
      }}
    >
      <Alert
        severity="info"
        variant="filled"
        onClose={() => setNeedRefresh(false)}
        action={
          <Button
            color="inherit"
            size="small"
            onClick={() => void updateServiceWorker(true)}
            sx={{ fontWeight: 600 }}
          >
            {t('update.reload')}
          </Button>
        }
      >
        {t('update.available')}
      </Alert>
    </Snackbar>
  )
}
